import { useState } from 'react';
import { overlay } from 'overlay-kit';

import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onCreate: (name: string) => Promise<void>;
  onUnmount: () => void;
};

function CreateListDialog({ isOpen, onCancel, onCreate, onUnmount }: Props) {
  const [name, setName] = useState('');
  const [isPending, setIsPending] = useState(false);

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount();
    }
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || isPending) return;

    setIsPending(true);
    try {
      await onCreate(trimmedName);
    } catch {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onCancel() : undefined)}>
      <DialogContent onAnimationEnd={handleAnimationEnd}>
        <DialogHeader>
          <DialogTitle>Create saved list</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          value={name}
          placeholder="List name"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleCreate();
            }
          }}
        />
        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            loading={isPending}
            disabled={name.trim().length === 0}
            onClick={handleCreate}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function showCreateListDialog({
  onCreate,
}: {
  onCreate: (name: string) => Promise<void>;
}): Promise<void> {
  return overlay.openAsync<void>(({ isOpen, close, unmount }) => (
    <CreateListDialog
      isOpen={isOpen}
      onCancel={close}
      onCreate={async (name) => {
        await onCreate(name);
        close();
      }}
      onUnmount={unmount}
    />
  ));
}
